import { Controller } from "@hotwired/stimulus"

// usage
/*
  <div data-controller="makase-tab">
    <ul>
      <li data-makase-tab-target="trigger" data-makase-tab-trigger-name="weekday">
        <a>평일</a>
      </li>
      <li data-makase-tab-target="trigger" data-makase-tab-trigger-name="weekend">
        <a>주말</a>
      </li>
    </ul>
    <div data-makase-tab-target="content" data-makase-tab-content-name="weekday">
      평일 콘텐츠
    </div>
    <div data-makase-tab-target="content" data-makase-tab-content-name="weekend">
      주말 콘텐츠
    </div>
    <div data-makase-tab-target="fallback">
      탭을 선택해 주세요.
    </div>
  </div>


  <!-- 사전 정의 스타일과 함께 -->
  <div data-controller="makase-tab">
    <ul data-makase-tab-style> <!-- or data-makase-tab-style="full" -->
      <li data-makase-tab-target="trigger" data-makase-tab-trigger-name="weekday">
        <a>평일</a>
      </li>
      <li data-makase-tab-target="trigger" data-makase-tab-trigger-name="weekend">
        <a>주말</a>
      </li>
    </ul>
    <div data-makase-tab-target="content" data-makase-tab-content-name="weekday" data-makase-tab-content-style="block">
      평일 콘텐츠
    </div>
    <div data-makase-tab-target="content" data-makase-tab-content-name="weekend" data-makase-tab-content-style="block">
      주말 콘텐츠
    </div>
    <div data-makase-tab-target="fallback" data-makase-tab-content-style="block">
      탭을 선택해 주세요.
    </div>
  </div>


  <!-- 고급 사용법 -->
  <div data-controller="makase-tab"
       data-makase-tab-select-value="weekday"
       data-makase-tab-trigger-value="click"
       data-makase-tab-closable-value="false">
    ...
  </div>

  <!-- select-value는 name 또는 index(숫자) 모두 가능 -->
  <div data-controller="makase-tab"
       data-makase-tab-select-value="0"><!-- 첫 번째 탭 -->
    ...
  </div>
  <div data-controller="makase-tab"
       data-makase-tab-select-value="weekday"><!-- 이름으로 선택 -->
    ...
  </div>

  <!-- fallback은 복수 지정 가능 -->
  <div data-controller="makase-tab" data-makase-tab-closable-value="true">
    <div data-makase-tab-target="fallback">메시지 1</div>
    <div data-makase-tab-target="fallback">메시지 2</div>
  </div>
*/

// data-makase-tab-trigger-name 과 data-makase-tab-content-name 은 정의하지 않으면 index로만 작동 됨

export default class MakaseTabController extends Controller {
  // this.triggerTarget
  // this.triggerTargets
  // this.contentTarget
  // this.contentTargets
  // this.fallbackTarget
  // this.fallbackTargets (복수 가능)
  static targets = ["trigger", "content", "fallback"]

  // this.selectValue : string // 현재 선택된 탭 (name 또는 index)
  // this.triggerValue : hover|click
  // this.closableValue : true|false  // 탭이 아무것도 선택하지 않은 상태가 가능한지
  // this.openTriggerAttributesValue : string // trigger에 적용할 속성명
  // this.hideTriggerAttributesValue : string // trigger에서 제거할 속성명
  // this.openContentAttributesValue : string // content에 적용할 속성명
  // this.hideContentAttributesValue : string // content에서 제거할 속성명
  static values = {
    select: { type: String, default: "" }, // 처음 선택될 탭, 현재 선택된 탭
    trigger: { type: String, default: "click" }, // hover 또는 click
    closable: { type: Boolean, default: false }, // true이면 탭 토글 가능
    openTriggerAttributes: { type: String, default: "aria-selected" },
    hideTriggerAttributes: { type: String, default: "" },
    openContentAttributes: { type: String, default: "aria-expanded" },
    hideContentAttributes: { type: String, default: "aria-hidden" },
  }

  connect() {
    this.currentIndex = -1

    // 이벤트 리스너 바인딩
    this.boundHandleTrigger = this.handleTrigger.bind(this)

    // trigger 이벤트 설정
    this.setupTriggerEvents()

    // 초기 탭 선택
    this.initializeTab()
  }

  disconnect() {
    this.removeTriggerEvents()
  }

  setupTriggerEvents() {
    this.triggerTargets.forEach((trigger) => {
      if (this.triggerValue === "hover") {
        trigger.addEventListener("mouseenter", this.boundHandleTrigger)
      } else {
        // click (기본)
        trigger.addEventListener("click", this.boundHandleTrigger)
      }
    })
  }

  removeTriggerEvents() {
    this.triggerTargets.forEach((trigger) => {
      if (this.triggerValue === "hover") {
        trigger.removeEventListener("mouseenter", this.boundHandleTrigger)
      } else {
        trigger.removeEventListener("click", this.boundHandleTrigger)
      }
    })
  }

  initializeTab() {
    // selectValue가 있으면 해당 탭 선택
    if (this.selectValue) {
      const index = this.findTabIndex(this.selectValue)
      if (index !== -1) {
        this.selectTab(index)
        return
      }
    }

    // closable이 false면 첫 번째 탭 선택
    if (!this.closableValue && this.triggerTargets.length > 0) {
      this.selectTab(0)
    } else {
      this.showFallback()
    }
  }

  handleTrigger(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const index = this.triggerTargets.indexOf(trigger)

    if (index === -1) return

    // closable이 true이고 현재 선택된 탭을 다시 클릭하면 닫기
    if (this.closableValue && this.currentIndex === index) {
      this.deselectAll()
      this.showFallback()
    } else {
      this.selectTab(index)
    }
  }

  selectTab(index) {
    if (index < 0 || index >= this.triggerTargets.length) return

    // 모든 탭 비활성화
    this.deselectAll()

    // 선택된 탭 활성화
    const trigger = this.triggerTargets[index]
    const content = this.contentTargets[index]

    if (trigger) {
      this.activateTrigger(trigger)
    }

    if (content) {
      this.showContent(content)
    }

    // fallback 숨기기
    this.hideFallback()

    // 현재 인덱스 업데이트
    this.currentIndex = index

    // selectValue 업데이트 (name 또는 index)
    const triggerName = trigger.dataset.makaseTabTriggerName
    this.selectValue = triggerName || String(index)
  }

  deselectAll() {
    this.triggerTargets.forEach((trigger) => {
      this.deactivateTrigger(trigger)
    })

    this.contentTargets.forEach((content) => {
      this.hideContent(content)
    })

    this.currentIndex = -1
    this.selectValue = ""
  }

  activateTrigger(trigger) {
    // 스타일 제어 (인라인)
    trigger.style.display = ""

    // 속성 제어
    if (this.openTriggerAttributesValue) {
      trigger.setAttribute(this.openTriggerAttributesValue, "true")
    }

    if (this.hideTriggerAttributesValue) {
      trigger.removeAttribute(this.hideTriggerAttributesValue)
    }
  }

  deactivateTrigger(trigger) {
    // 속성 제어
    if (this.openTriggerAttributesValue) {
      trigger.setAttribute(this.openTriggerAttributesValue, "false")
    }

    if (this.hideTriggerAttributesValue) {
      trigger.setAttribute(this.hideTriggerAttributesValue, "true")
    }
  }

  showContent(content) {
    // 스타일 제어 (인라인)
    content.style.display = ""

    // 속성 제어
    if (this.openContentAttributesValue) {
      content.setAttribute(this.openContentAttributesValue, "true")
    }

    if (this.hideContentAttributesValue) {
      content.removeAttribute(this.hideContentAttributesValue)
    }
  }

  hideContent(content) {
    // 스타일 제어 (인라인)
    content.style.display = "none"

    // 속성 제어
    if (this.openContentAttributesValue) {
      content.setAttribute(this.openContentAttributesValue, "false")
    }

    if (this.hideContentAttributesValue) {
      content.setAttribute(this.hideContentAttributesValue, "true")
    }
  }

  showFallback() {
    if (this.hasFallbackTarget) {
      this.fallbackTargets.forEach((fallback) => {
        fallback.style.display = ""
      })
    }
  }

  hideFallback() {
    if (this.hasFallbackTarget) {
      this.fallbackTargets.forEach((fallback) => {
        fallback.style.display = "none"
      })
    }
  }

  findTabIndex(nameOrIndex) {
    // 숫자인 경우 index로 처리
    const numIndex = parseInt(nameOrIndex, 10)
    if (!isNaN(numIndex) && numIndex >= 0 && numIndex < this.triggerTargets.length) {
      return numIndex
    }

    // name으로 찾기
    return this.triggerTargets.findIndex((trigger) => {
      return trigger.dataset.makaseTabTriggerName === nameOrIndex
    })
  }

  // 값 변경 감지
  selectValueChanged() {
    if (!this.selectValue) {
      if (this.closableValue) {
        this.deselectAll()
        this.showFallback()
      }
      return
    }

    const index = this.findTabIndex(this.selectValue)
    if (index !== -1 && index !== this.currentIndex) {
      this.selectTab(index)
    }
  }

  // 외부에서 호출 가능한 메서드
  selectByName(name) {
    const index = this.findTabIndex(name)
    if (index !== -1) {
      this.selectTab(index)
    }
  }

  selectByIndex(index) {
    this.selectTab(index)
  }
}
